import React, { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export type MessageParams = {
    id?: string;
    className?: string | null;
    message?: ReactNode | string | null;
    messageKey?: string | null;
    raw?: boolean; // True to show the message (string) as raw HTML (warning: dangerous! -- never allow user submitted content)
    fullWidth?: boolean;
    hideAfter?: number; // Milliseconds to wait before invoking the onClick method
    onClick?(): void;
};
type InternalMessageParams = MessageParams & {
    icon: string;
    className: string;
};

const ampRe = /&/g;
const ltRe = /</g;
const gtRe = />/g;
const crlfRe = /\r?\n/g;

export const Raw = ({ text = "" }: { text?: string }) =>
    <pre
        dangerouslySetInnerHTML={ {
            __html: text
                .replace(ampRe, '&amp;')
                .replace(ltRe, '&lt;')
                .replace(gtRe, '&gt;')
                .replace(crlfRe, '<br/>'),
        } }
    />
;

const Message = ({ id, message: _message, messageKey, icon, className, onClick, raw, fullWidth, hideAfter = 3000 }: InternalMessageParams) => {
    const { t } = useTranslation();
    const timerRef = useRef<number>();

    const message = _message || (messageKey ? t([ `ERROR.${messageKey}`, messageKey, 'SERVER_ERROR' ]) : null);

    useEffect(() => {
        if (message && onClick && hideAfter) {
            timerRef.current = window.setTimeout(onClick, hideAfter);
            return () => {
                clearTimeout(timerRef.current!);
            };
        }
    }, [ message, hideAfter, onClick, timerRef ]);

    if (!message) {
        return null;
    }
    return (
        <div
            id={ id }
            className={ `ui compact ${className} message` }
            onClick={ onClick }
            style={ fullWidth ? { width: '100%' } : undefined }
        >
            <i className={ `${icon} icon` }/>
            <span>
                { raw ? <Raw text={ message as string }/> : message }
            </span>
        </div>
    );
};

export const InfoMessage = ({ className, ...params }: MessageParams) =>
    <Message { ...params } icon="info circle" className={ `info ${className || ''}` }/>
;

export const SuccessMessage = ({ className, ...params }: MessageParams) =>
    <Message { ...params } icon="checkmark" className={ `positive ${className || ''}` }/>
;

export const WarningMessage = ({ className, ...params }: MessageParams) =>
    <Message { ...params } icon="warning sign" className={ `yellow ${className || ''}` }/>
;

export const ErrorMessage = ({ className, ...params }: MessageParams) =>
    <Message { ...params } icon="warning sign" className={ `negative ${className || ''}` }/>
;
